import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Button, LinearProgress, Paper, Card, CardContent, Alert, IconButton, Fab, Slider } from '@mui/material';
import { QuestionItem } from './components/questions';
import {
    NavigateBefore as PrevIcon,
    NavigateNext as NextIcon,
    SkipNext as SkipIcon,
    VolumeUp as VolumeIcon,
    PlayArrow as PlayIcon,
    Pause as PauseIcon
} from '@mui/icons-material';
import ReactPlayer from 'react-player';
import WaveSurfer from 'wavesurfer.js';
import { Document, Page } from 'react-pdf';
import { resolveMediaUrl } from './stimulusOrder';

const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

// Keep this outside the component so memoized stimulus normalization does not
// change identity on every render. A changing currentFile object was enough to
// retrigger the `shown` telemetry effect repeatedly, which can flood the
// browser with /stimulus-event requests and starve actual image downloads.
const inferCategory = (f = {}) => {
    if (f.category) return f.category;
    if (f.mediaMeta?.category) return f.mediaMeta.category;
    const name = f.originalName || f.filename || f.name || f.mediaMeta?.originalName || '';
    const lower = name.toLowerCase();
    if (/\.(png|jpg|jpeg|gif|bmp|webp)$/.test(lower)) return 'image';
    if (/\.(mp4|mov|webm|mkv)$/.test(lower)) return 'video';
    if (/\.(mp3|wav|ogg|m4a)$/.test(lower)) return 'audio';
    if (/\.(pdf)$/.test(lower)) return 'pdf';
    if (/\.(txt|md)$/.test(lower)) return 'text';
    return 'file'; // generic
};

const noopStimulusEvent = () => {};

export default function StimuliComponent({ stimuliSet, stimuli: inlineStimuli = [], questions = [], settings = {}, onComplete, onStimulusEvent }) {
    // A7 — defensive shim so callers without the prop still work.
    const emitStimulusEvent = onStimulusEvent || noopStimulusEvent;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [responses, setResponses] = useState({});
    const [allResponses, setAllResponses] = useState([]);
    const [viewStartTime, setViewStartTime] = useState(Date.now());
    const [minTimeElapsed, setMinTimeElapsed] = useState(false);
    const [autoAdvanceTimer, setAutoAdvanceTimer] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(0.5);
    const [textContent, setTextContent] = useState('');
    const [pdfPage, setPdfPage] = useState(1);
    const [pdfNumPages, setPdfNumPages] = useState(null);
    const [error, setError] = useState('');

    const audioContainerRef = useRef(null);
    const waveSurferRef = useRef(null);
    const playerRef = useRef(null);

    const providedStimuliSet = stimuliSet || { _id: 'inline', name: 'Inline Stimuli' };
    const rawFiles = React.useMemo(() => {
        // Runtime order comes from the server's participant-specific
        // materialized block snapshot. Avoid client-side re-randomization so
        // displayed stimuli, telemetry indices, and submitted responses all
        // refer to the same order the server knows about.
        return (Array.isArray(inlineStimuli) ? inlineStimuli : []).map((stimulus, displayIndex) => ({
            ...stimulus,
            __displayIndex: typeof stimulus.__displayIndex === 'number' ? stimulus.__displayIndex : displayIndex,
            __originalIndex: typeof stimulus.__originalIndex === 'number' ? stimulus.__originalIndex : displayIndex,
        }));
    }, [inlineStimuli]);
    
    // Normalize each file once per actual stimuli/order change. Keeping these
    // object identities stable prevents the index-change effect below from
    // firing again after unrelated state updates such as setViewStartTime().
    const files = React.useMemo(() => rawFiles.map(f => {
        const url = f.url || f.src || f.path || f.file || f.media || (f.mediaMeta && f.mediaMeta.url);
        const originalName = f.originalName || f.mediaMeta?.originalName || f.label || f.filename;
        const mergedQuestions = f.questions || f.itemQuestions || [];
        return {
            ...f,
            url,
            originalName,
            questions: mergedQuestions,
            category: inferCategory(f)
        };
    }), [rawFiles]);
    const currentFile = files[currentIndex];
    const currentStimulusId = currentFile?.id || currentFile?._id
        || currentFile?.mediaMeta?.id || `stim-${currentIndex}`;
    const currentStimulusKey = currentFile
        ? `${currentIndex}:${typeof currentStimulusId === 'string' ? currentStimulusId : String(currentStimulusId)}:${currentFile.__originalIndex ?? currentIndex}`
        : `missing:${currentIndex}`;
    
    const {
        showProgress = true,
        allowSkip = false,
        minViewTime = 0,
        autoAdvance = false,
        autoAdvanceTime = 0
    } = settings;

    // Function definitions first
    const resetMediaStates = useCallback(() => {
        setPdfPage(1);
        setIsPlaying(false);
        setTextContent('');
        if (waveSurferRef.current) {
            waveSurferRef.current.stop();
        }
    }, []);

    // Combine block-level questions with stimulus-level questions for current stimulus
    const combinedQuestions = React.useMemo(() => {
        const stimulusSpecific = currentFile?.questions || [];
        const merged = [...(questions || []), ...stimulusSpecific];
        console.debug('[StimuliComponent] combinedQuestions for stimulus', currentIndex, merged);
        return merged;
    }, [questions, currentFile, currentIndex]);

    const validateResponses = useCallback(() => {
        for (const q of combinedQuestions) {
            const val = responses[q.id];
            // Required check for emptiness
            if (q.required && (val === undefined || val === '' || (Array.isArray(val) && val.length === 0))) {
                return false;
            }
            // Min/Max enforcement for multiple selection
            if ((q.type === 'multiple_selection' || q.type === 'checkbox') && Array.isArray(val)) {
                const ms = q.multipleSelection || q.selectionConfig || {};
                const min = Number.isFinite(ms.min) ? ms.min : (ms.min === 0 ? 0 : null);
                const max = Number.isFinite(ms.max) ? ms.max : null;
                if (min !== null && val.length < min) return false;
                if (max !== null && val.length > max) return false;
            }
        }
        return true;
    }, [combinedQuestions, responses]);

    const handleComplete = useCallback((finalResponses) => {
        // Prepare the response data in the same format as questionnaires
        const completionData = {
            stimuliSetId: providedStimuliSet._id,
            stimuliSetName: providedStimuliSet.name,
            responses: finalResponses || allResponses,
            completedAt: new Date().toISOString(),
            totalTime: Date.now() - viewStartTime,
            type: 'stimuli'
        };
        
        console.log('Stimuli block completed, submitting responses:', completionData);
        // A7: emit set_complete so the data team can detect natural
        // completion vs early termination by absence of this event.
        emitStimulusEvent('set_complete', {
            totalTime: completionData.totalTime,
            count: (finalResponses || allResponses || []).length,
        }, {});
        
        // Call onComplete to pass data to parent (Experiment.js) for HTTP submission
        if (onComplete) {
            onComplete(completionData);
        }
    }, [allResponses, viewStartTime, onComplete, providedStimuliSet._id, providedStimuliSet.name, emitStimulusEvent]);

    const initializeWaveSurfer = useCallback(() => {
        if (waveSurferRef.current) {
            waveSurferRef.current.destroy();
        }
        
        waveSurferRef.current = WaveSurfer.create({
            container: audioContainerRef.current,
            waveColor: '#ddd',
            progressColor: '#1976d2',
            cursorColor: '#1976d2',
            barWidth: 2,
            barRadius: 3,
            responsive: true,
            height: 60,
            normalize: true
        });

        // Construct full URL for audio files (guard against missing url)
        const rawUrl = currentFile?.url || currentFile?.src || currentFile?.path || '';
        if (!rawUrl) {
            console.warn('[StimuliComponent] audio file missing url/src/path field:', currentFile);
            return;
        }
        const audioUrl = resolveMediaUrl(rawUrl, API_URL);
        
        waveSurferRef.current.load(audioUrl);
        
        waveSurferRef.current.on('ready', () => {
            setIsPlaying(false);
        });
        
        waveSurferRef.current.on('play', () => setIsPlaying(true));
        waveSurferRef.current.on('pause', () => setIsPlaying(false));
    }, [currentFile]);

    const handleNext = useCallback(() => {
        if (!minTimeElapsed && !allowSkip) {
            setError(`Please view this stimulus for at least ${minViewTime} seconds`);
            return;
        }
        
        if (combinedQuestions.length > 0 && !validateResponses()) {
            setError('Please answer all required questions');
            return;
        }
        
        // Clear auto advance timer
        if (autoAdvanceTimer) {
            clearTimeout(autoAdvanceTimer);
            setAutoAdvanceTimer(null);
        }
        
        // Record response data for this stimulus
        const viewTime = Date.now() - viewStartTime;
        const responseData = {
            stimulusIndex: currentIndex,
            originalStimulusIndex: typeof currentFile?.__originalIndex === 'number' ? currentFile.__originalIndex : currentIndex,
            stimulusFile: currentFile,
            responses: responses,
            viewTime,
            timestamp: new Date().toISOString()
        };
        // A7: emit a `hidden` event before we tear down the current
        // stimulus. Pairs with the `shown` event from the index-change
        // effect below so the reader can compute exposure_ms reliably
        // (Date.now() math here matches the existing viewTime field).
        emitStimulusEvent('hidden', { viewTime }, {
            stimIndex: currentIndex,
            stimulusId: typeof currentStimulusId === 'string' ? currentStimulusId : String(currentStimulusId),
        });
        
        // Add this stimulus response to our collection
        const updatedAllResponses = [...allResponses, responseData];
        setAllResponses(updatedAllResponses);
        
        // Move to next stimulus or complete
        if (currentIndex < files.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setResponses({});
            setError('');
            resetMediaStates();
        } else {
            // All stimuli completed - submit all responses via onComplete
            handleComplete(updatedAllResponses);
        }
    }, [minTimeElapsed, allowSkip, minViewTime, combinedQuestions.length, validateResponses, autoAdvanceTimer, currentIndex, currentFile, currentStimulusId, responses, viewStartTime, allResponses, files.length, resetMediaStates, handleComplete, emitStimulusEvent]);

    // useEffects after function definitions
    useEffect(() => {
        // Start viewing timer when stimulus changes
        setViewStartTime(Date.now());
        setMinTimeElapsed(false);
        // A7: emit `shown` exactly once per stimulus transition. The
        // payload captures the inferred category and original filename
        // so the data team can group by media type without re-joining.
        if (currentFile) {
            emitStimulusEvent('shown', {
                category: currentFile.category || null,
                originalName: currentFile.originalName || null,
            }, {
                stimIndex: currentIndex,
                stimulusId: typeof currentStimulusId === 'string' ? currentStimulusId : String(currentStimulusId),
            });
        }
        
        // Reset media states and clear text content when stimulus changes
        resetMediaStates();
        // Scroll to top to ensure stimulus is visible first
        try {
            if (typeof window !== 'undefined') {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                // Also try scrolling the nearest scrollable container
                const el = document.querySelector('main, #root, body');
                if (el && typeof el.scrollTo === 'function') {
                    el.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                }
            }
        } catch (_) {}
        
        // Set minimum view time timer
        if (minViewTime > 0) {
            const timer = setTimeout(() => {
                setMinTimeElapsed(true);
            }, minViewTime * 1000);
            
            return () => clearTimeout(timer);
        } else {
            setMinTimeElapsed(true);
        }
    }, [currentStimulusKey, currentIndex, minViewTime, resetMediaStates, currentFile, currentStimulusId, emitStimulusEvent]);

    useEffect(() => {
        // Auto advance timer
        if (autoAdvance && autoAdvanceTime > 0 && currentFile) {
            const timer = setTimeout(() => {
                handleNext();
            }, autoAdvanceTime * 1000);
            
            setAutoAdvanceTimer(timer);
            return () => clearTimeout(timer);
        }
    }, [currentIndex, autoAdvance, autoAdvanceTime, currentFile, handleNext]);

    useEffect(() => {
        // Initialize audio waveform for audio files
        if (currentFile?.category === 'audio' && audioContainerRef.current) {
            initializeWaveSurfer();
        }
        
        return () => {
            if (waveSurferRef.current) {
                waveSurferRef.current.destroy();
                waveSurferRef.current = null;
            }
        };
    }, [currentFile, initializeWaveSurfer]);

    const handleResponseChange = (questionId, value) => {
        setResponses(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    // Function to fetch text content
    const fetchTextContent = async (url) => {
        try {
            const response = await fetch(url);
            const text = await response.text();
            setTextContent(text);
        } catch (error) {
            console.error('Error fetching text content:', error);
            setError('Failed to load text file');
        }
    };

    const handleSkip = () => {
        if (allowSkip) {
            handleNext();
        }
    };

    const renderStimulus = () => {
        if (!currentFile) {
            return (
                <Typography variant="h6" color="textSecondary">
                    No stimulus to display
                </Typography>
            );
        }

    const { category, originalName, title } = currentFile;
        // Flexibly pick a URL-like field
        const rawUrl = currentFile.url || currentFile.src || currentFile.path || currentFile.file || '';
        // Ensure we use full URLs for all media types (if present)
        const fullUrl = resolveMediaUrl(rawUrl, API_URL);
        if (!rawUrl && category !== 'text') {
            console.warn('[StimuliComponent] Stimulus missing url-like field (url|src|path|file). File object:', currentFile);
        }

        switch (category) {
            case 'image':
                const imageWidth = Number(currentFile?.size?.width || currentFile?.width || 0);
                return (
                    <Box sx={{ textAlign: 'center' }}>
                        <img
                            src={fullUrl}
                            alt={title || originalName}
                            style={{
                                width: imageWidth > 0 ? `calc(${imageWidth}px * var(--stimulus-scale-factor, 1))` : 'auto',
                                maxWidth: '100%',
                                maxHeight: '60vh',
                                objectFit: 'contain',
                                display: 'block',
                                margin: '0 auto'
                            }}
                        />
                    </Box>
                );

            case 'video':
                return (
                    <Box sx={{ textAlign: 'center' }}>
                        <ReactPlayer
                            ref={playerRef}
                            url={fullUrl}
                            width="100%"
                            height="60vh"
                            controls
                            playing={isPlaying}
                            volume={volume}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            onEnded={() => setIsPlaying(false)}
                        />
                    </Box>
                );

            case 'audio':
                return (
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {title || originalName}
                        </Typography>
                        <Box ref={audioContainerRef} sx={{ mb: 2, mx: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                            <IconButton
                                onClick={() => {
                                    if (waveSurferRef.current) {
                                        waveSurferRef.current.playPause();
                                    }
                                }}
                                size="large"
                            >
                                {isPlaying ? <PauseIcon /> : <PlayIcon />}
                            </IconButton>
                            <VolumeIcon />
                            <Slider
                                value={volume}
                                onChange={(_, value) => {
                                    setVolume(value);
                                    if (waveSurferRef.current) {
                                        waveSurferRef.current.setVolume(value);
                                    }
                                    // A7: record volume change as an
                                    // interaction event so the data team
                                    // can audit whether the participant
                                    // ever touched audio playback.
                                    emitStimulusEvent('interaction', {
                                        subkind: 'volume_change',
                                        value,
                                    }, { stimIndex: currentIndex });
                                }}
                                min={0}
                                max={1}
                                step={0.1}
                                sx={{ width: 100 }}
                            />
                        </Box>
                    </Box>
                );

            case 'pdf':
                // Debug the PDF URL
                if (fullUrl) {
                    debugPdfUrl(fullUrl);
                }
                
                return (
                    <Box sx={{ 
                        textAlign: 'center', 
                        margin: 0, 
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%'
                    }}>
                        <Document
                            file={fullUrl}
                            onLoadSuccess={({ numPages }) => {
                                console.log('PDF loaded successfully:', { numPages, url: fullUrl });
                                setPdfNumPages(numPages);
                                setError(''); // Clear any previous errors
                            }}
                            onLoadError={(error) => {
                                console.error('PDF load error:', error);
                                console.log('Attempted to load URL:', fullUrl);
                                setError(`Failed to load PDF: ${error.message || 'Invalid PDF file'}`);
                            }}
                            onLoadProgress={({ loaded, total }) => {
                                console.log('PDF loading progress:', { loaded, total, url: fullUrl });
                            }}
                            loading={
                                <Box sx={{ p: 4 }}>
                                    <Typography variant="body1">Loading PDF...</Typography>
                                    <LinearProgress sx={{ mt: 2 }} />
                                </Box>
                            }
                            error={
                                <Box sx={{ p: 4 }}>
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        Failed to load PDF file. Please check that the file is a valid PDF.
                                    </Alert>
                                    <Typography variant="body2" color="text.secondary">
                                        File URL: {fullUrl}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        File name: {originalName}
                                    </Typography>
                                </Box>
                            }
                        >
                            <Page 
                                pageNumber={pdfPage}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                onLoadError={(error) => {
                                    console.error('PDF page load error:', error);
                                    setError(`Failed to load PDF page: ${error.message}`);
                                }}
                                onRenderError={(error) => {
                                    console.error('PDF page render error:', error);
                                    setError(`Failed to render PDF page: ${error.message}`);
                                }}
                            />
                        </Document>
                        {/* PDF navigation controls */}
                        <Box sx={{ 
                            mt: 1, 
                            mb: 0,
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            gap: 1,
                            padding: 0,
                            width: '100%'
                        }}>
                            <IconButton
                                onClick={() => {
                                    const next = Math.max(1, pdfPage - 1);
                                    setPdfPage(next);
                                    // A7: PDF page navigation as an interaction event.
                                    emitStimulusEvent('interaction', {
                                        subkind: 'pdf_page_change',
                                        from: pdfPage,
                                        to: next,
                                    }, { stimIndex: currentIndex });
                                }}
                                disabled={pdfPage <= 1}
                            >
                                <PrevIcon />
                            </IconButton>
                            <Typography variant="body2">
                                Page {pdfPage} of {pdfNumPages || '?'}
                            </Typography>
                            <IconButton
                                onClick={() => {
                                    const next = Math.min(pdfNumPages || 1, pdfPage + 1);
                                    setPdfPage(next);
                                    emitStimulusEvent('interaction', {
                                        subkind: 'pdf_page_change',
                                        from: pdfPage,
                                        to: next,
                                    }, { stimIndex: currentIndex });
                                }}
                                disabled={pdfPage >= (pdfNumPages || 1)}
                            >
                                <NextIcon />
                            </IconButton>
                        </Box>
                    </Box>
                );

            case 'text':
                // Use inline content if provided; else fetch from URL
                const inlineContent = currentFile.content || currentFile.text || null;
                if (!inlineContent && !textContent && fullUrl) {
                    fetchTextContent(fullUrl);
                }
                return (
                    <Box sx={{
                        maxHeight: '60vh',
                        overflow: 'auto',
                        p: 3,
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography
                            component="pre"
                            sx={{
                                fontFamily: 'Calibri, Arial, sans-serif',
                                fontSize: '16px',
                                lineHeight: '1.5',
                                color: '#000000',
                                backgroundColor: '#ffffff',
                                margin: 0,
                                padding: 0,
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word'
                            }}
                        >
                            {inlineContent || textContent || (fullUrl ? 'Loading text...' : 'No text content provided')}
                        </Typography>
                    </Box>
                );

            default:
                return (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography variant="h6" color="textSecondary" sx={{ mb:1 }}>
                            Unsupported file type: {category}
                        </Typography>
                        <Typography variant="body2" sx={{ mb:1 }}>
                            File: {originalName || '(no originalName)'}
                        </Typography>
                        <Typography variant="caption" component="pre" sx={{ textAlign:'left', maxHeight:200, overflow:'auto', p:1, bgcolor:'#fafafa', border:'1px solid #eee' }}>
                            {JSON.stringify(currentFile, null, 2)}
                        </Typography>
                    </Box>
                );
        }
    };

    const renderQuestions = () => {
        if (combinedQuestions.length === 0) return null;

        return (
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Questions
                    </Typography>
                    {combinedQuestions.map((question, index) => (
                        <Box key={question.id} sx={{ mb: 3 }}>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                {index + 1}. {question.text}
                                {question.required && <span style={{ color: 'red' }}>*</span>}
                            </Typography>
                            
                            <QuestionItem
                                question={question}
                                value={responses[question.id]}
                                onChange={(v) => handleResponseChange(question.id, v)}
                            />
                        </Box>
                    ))}
                </CardContent>
            </Card>
        );
    };

    // Debug function to check PDF URL
    const debugPdfUrl = async (url) => {
        try {
            console.log('Fetching PDF URL to check content type:', url);
            const response = await fetch(url, { method: 'HEAD' });
            console.log('PDF URL response headers:', {
                contentType: response.headers.get('content-type'),
                contentLength: response.headers.get('content-length'),
                status: response.status,
                statusText: response.statusText
            });
            
            // If it's not a PDF, let's see what we're actually getting
            if (!response.headers.get('content-type')?.includes('pdf')) {
                const textResponse = await fetch(url);
                const text = await textResponse.text();
                console.log('Non-PDF content received:', text.substring(0, 500));
            }
        } catch (error) {
            console.error('Error checking PDF URL:', error);
        }
    };

    if (!files.length) {
        console.warn('[StimuliComponent] No stimuli available. providedStimuliSet:', providedStimuliSet, 'inlineStimuli length:', inlineStimuli?.length);
        return (
            <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h6" color="textSecondary">
                    No stimuli available
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
            {/* Progress Bar */}
            {showProgress && (
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">
                            Stimulus {currentIndex + 1} of {files.length}
                        </Typography>
                        <Typography variant="body2">
                            {Math.round(((currentIndex + 1) / files.length) * 100)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={((currentIndex + 1) / files.length) * 100}
                    />
                </Box>
            )}

            {/* Error Display (moved to bottom near navigation; top suppressed) */}

            {/* Stimulus Title */}
            {currentFile?.title && (
                <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
                    {currentFile.title}
                </Typography>
            )}

            {/* Stimulus Description */}
            {currentFile?.description && (
                <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
                    {currentFile.description}
                </Typography>
            )}

            {/* Main Stimulus Display */}
            <Paper sx={{ 
                p: currentFile?.category === 'pdf' ? 1 : 3, 
                mb: 2 
            }}>
                {renderStimulus()}
            </Paper>

            {/* Questions */}
            {renderQuestions()}

            {/* Navigation + Error */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 3, flexWrap:'wrap', gap:2 }}>
                <Box>
                    {allowSkip && (
                        <Button
                            variant="outlined"
                            onClick={handleSkip}
                            startIcon={<SkipIcon />}
                        >
                            Skip
                        </Button>
                    )}
                </Box>
                <Box sx={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:1, flexGrow:1 }}>
                    {error && (
                        <Alert severity="warning" sx={{ order:0, width:'100%' }}>
                            {error}
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, order:1 }}>
                        {!minTimeElapsed && (
                            <Typography variant="body2" color="textSecondary">
                                Please view for {minViewTime} seconds
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!minTimeElapsed && !allowSkip}
                            size="large"
                        >
                            {currentIndex < files.length - 1 ? 'Next' : 'Complete'}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Auto-advance indicator */}
            {autoAdvance && autoAdvanceTime > 0 && (
                <Box sx={{ position: 'fixed', bottom: 20, right: 20 }}>
                    <Fab color="primary" size="small">
                        <Typography variant="caption">
                            Auto: {autoAdvanceTime}s
                        </Typography>
                    </Fab>
                </Box>
            )}
        </Box>
    );
};

// default export moved to top-level function declaration
